<template>
  <div class="modal" :class="{ 'modal--no-padding': !showBackground }">
    <!-- D-237 (A4): `aria-hidden` on the BACKDROP, which is a SIBLING of `.modal__content`
         — safe here and catastrophic one level up: on `.modal` it would hide the dialog
         itself. It stays a mouse-only click-to-close affordance and is deliberately NOT
         made focusable; the keyboard equivalent is ESC, which this component has. -->
    <div
      v-if="showBackground"
      class="modal__background"
      aria-hidden="true"
      @click="close"
      :style="modalStyles"
    ></div>
    <!-- 🪤 `aria-label`, not `aria-labelledby`. The title arrives through the `header`
         SLOT, so this component cannot know whether one exists, what element it is, or
         whether it has an id — three of the four call sites pass one and the classic
         search modal passes none. A label the caller supplies is the only thing that is
         true at every site. -->
    <div
      class="modal__content"
      ref="dialog"
      role="dialog"
      aria-modal="true"
      :aria-label="label"
      tabindex="-1"
      :class="{ 'modal__content--centered': centered }"
      :style="contentWidth"
    >
      <button v-if="!hideCloseButton" class="modal__button" @click="close">
        <div class="is-sr-only">Close</div>
        <!-- D-243: `alt="Close icon"` DUPLICATED the sr-only "Close" above, so the button
             announced "Close Close icon". Decorative. -->
        <img :src="closeIconSrc" alt="" />
      </button>
      <slot name="header"></slot>
      <slot name="body"></slot>
      <slot name="footer"></slot>
    </div>
  </div>
</template>

<script>
// Vue1 `require('...svg')` -> ES-module import so Vite resolves the asset
// (inlined as a data URI under the build's assetsInlineLimit).
import closeSvg from '../../assets/v3-assets/close.svg';
import { createFocusTrap } from '../../shared-components/focusTrap';

export default {
  name: 'modal',
  // 🔴 D-237 (A4): THIS COMPONENT IS WHERE THE TRAP CAME FROM, and it is also the site of
  // the defect that made the extraction non-trivial. It used to arm in `mounted()`, and
  // `v3 PlayerInfo` renders `<share v-show=…>` while `Share` mounts this
  // UNCONDITIONALLY — so ESC and the trap armed at PLAYER LOAD, the focusable snapshot
  // was taken while the subtree was `display: none`, and nothing re-ran when the overlay
  // appeared. Keyed on `shown` now, which the caller must thread down when it controls
  // visibility from outside (see `Share` and `PlayerInfo`).
  mixins: [createFocusTrap({
    shown() { return this.shown; },
    close() { this.close(); },
  })],
  // Vue3 (LP-4/MR-78 emits parity): declare `close` so parent `@close` listeners
  // bind as a component event, not native-attr fallthrough.
  emits: ['close'],
  props: {
    backgroundColor: {
      type: String,
      default() {
        return '#e5e5e5';
      },
    },
    centered: {
      type: Boolean,
      default() {
        return false;
      },
    },
    hideCloseButton: Boolean,
    // D-237 (A4). Defaults TRUE so the three `v-if`-mounted call sites need no change:
    // for them, mounted is shown. Only a caller that keeps this component mounted while
    // hiding it — `Share`, under `PlayerInfo`'s `v-show` — has to pass it.
    shown: {
      type: Boolean,
      default() {
        return true;
      },
    },
    // The dialog's accessible name. Required in practice: a dialog with no name is
    // announced as "dialog" and nothing else.
    label: {
      type: String,
      default() {
        return 'Dialog';
      },
    },
    width: Number,
    showBackground: {
      type: Boolean,
      default() {
        return true;
      },
    },
  },
  // D-237 (A4): `mounted`/`beforeUnmount` and the whole `data` bag below them are GONE,
  // replaced by the mixin. What was here: a window ESC listener armed at mount, a
  // `trap()` that snapshotted `elements` ONCE, a `release()` nothing ever called, an
  // element keydown listener nothing ever removed, and a `focusableElements` list that
  // enumerated five input types and therefore skipped `range` and `email`. Each is fixed
  // in `shared-components/focusTrap.js` and named there.
  computed: {
    closeIconSrc() {
      return closeSvg;
    },

    modalStyles() {
      return {
        backgroundColor: this.backgroundColor,
      };
    },

    contentWidth() {
      if (!this.width) return null;

      return {
        minWidth: `${this.width}px`,
      };
    },
  },
  methods: {
    close() {
      this.$emit('close');
    },

  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/_helpers';

.modal {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  padding: 1rem;
  z-index: 9;
  &--no-padding {
    padding: 0;
  }
  &__background {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: #e5e5e5;
  }
  &__button {
    position: absolute;
    top: 1.5rem;
    right: 1.5rem;
    background: transparent;
    border: 0;
    cursor: pointer;
  }
  &__content {
    width: 100%;
    max-width: 100%;
    height: 100%;
    position: relative;
    background: #fff;
    border-radius: 0.5rem;
    padding: 1rem;
    line-height: 1.5;
    overflow-y: auto;
    &--centered {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      height: auto;
      width: auto;
    }
  }
}
</style>
